import _ from 'lodash'
import React, {FC, PropsWithChildren, ReactElement} from 'react'

import {TFunction, useTranslation} from '@/i18n'

import {sanitizeKeysOfValues} from '../values'

const isNumericKeyRegex = /^(\d+)$/
const isNumericChildKeyRegex = /^\.(\d+)$/

type TransProps = {
  i18nKey?: string
  t?: TFunction
  ns?: string
  components?: Record<string | number, ReactElement> | Array<ReactElement | undefined>
  values?: Record<string, string | number | null | undefined>
}

const createTagComponentFunc = (element: ReactElement) => {
  if (typeof element.type === 'string') {
    // "static" react element child
    return (chunks: React.ReactNode) => React.cloneElement(element, {}, chunks)
  } else if (typeof element.type === 'function') {
    // functional element child
    return (chunks: React.ReactNode) =>
      (element.type as React.FC<{children: React.ReactNode}>)({
        ...element.props,
        children: chunks,
      })
  } else if (
    typeof element.type === 'object' &&
    'render' in element.type &&
    typeof (element.type as any).render === 'function'
  ) {
    // functional element child with 'render' function in element.type.render
    return (chunks: React.ReactNode) =>
      ((element.type as any).render as React.FC<{children: React.ReactNode}>)({
        ...element.props,
        children: chunks,
      })
  } else {
    // unknown child
    return () => ''
  }
}

const Trans: FC<PropsWithChildren<TransProps>> = ({
  i18nKey,
  t,
  ns,
  components,
  values,
  children,
}) => {
  const {t: fallbackT} = useTranslation(ns)

  const sanitizedValues = values ? sanitizeKeysOfValues(values) : undefined

  const componentsFromProp = Array.isArray(components)
    ? Object.fromEntries(
        components.map((component, index) => [
          `tag${index}`,
          component ? createTagComponentFunc(component as ReactElement) : undefined,
        ]),
      )
    : _.mapValues(
        _.mapKeys(components, (_component, key) =>
          key.match(isNumericKeyRegex) ? `tag${key}` : key,
        ),
        (component) => createTagComponentFunc(component as ReactElement),
      )

  const componentsFromChildren = Object.fromEntries(
    React.Children.toArray(children)
      .map((child) => ({
        match:
          typeof child === 'object' && 'key' in child
            ? isNumericChildKeyRegex.exec(child.key!)
            : undefined,
        component: child,
      }))
      .filter((child) => child.match)
      .map((child) => [
        `tag${child.match![1]}`,
        createTagComponentFunc(child.component as ReactElement),
      ]),
  )

  return (t ?? fallbackT).rich?.(i18nKey, {
    ...sanitizedValues,
    ...componentsFromProp,
    ...componentsFromChildren,
  })
}

export default Trans
